import { useCallback, useEffect, useRef, useState } from "react";
import { GenericColumnModel, RemoteOptionColumnModel, RemoteOptionsColumnModel } from "./models";
import { IdNameResponseItemsResponseModel } from "services/tenantManagementService";

export const useRemoteColumns = (visibleColumns: GenericColumnModel[], rowsData: any[]) => {
	const [fetchingColumns, setFetchingColumns] = useState<any[]>([]);
 	const [columnValuesMap, setColumnValuesMap] = useState<any>({});
 	const [columnItemsMap, setColumnItemsMap] = useState({});
	// we don't want to listen to columnValuesMap changes, so using ref to avoid dependency
	const currentColumnValuesMapRef = useRef();
	currentColumnValuesMapRef.current = columnValuesMap;

	const getUniqueColumnValues = useCallback(
		() => {
			const valuesMap: {[key: string]: any[]} = {}

			for (const column of visibleColumns) {
				if (column instanceof RemoteOptionsColumnModel) {
					for (const rowData of rowsData) {
						const value = rowData[column.id];
						if (value === undefined || value.length === 0) {
							continue;
						}

						if (valuesMap[column.id] === undefined) {
							valuesMap[column.id] = [...value];
						} else {
							for (const valueId of value) {
								if (!valuesMap[column.id].includes(valueId)) {
									valuesMap[column.id].push(valueId);
								}

							}
						}
					}
				} else if (column instanceof RemoteOptionColumnModel) {
					for (const rowData of rowsData) {
						const value = rowData[column.id];
						if (value === undefined) {
							continue;
						}

						if (valuesMap[column.id] === undefined) {
							valuesMap[column.id] = [value];
						} else if (!valuesMap[column.id].includes(value)) {
							valuesMap[column.id].push(value);
						}
					}
				}
			}

			return valuesMap;
		},
		[rowsData, visibleColumns]
	)

	const getMissingColumnValues = useCallback(
		(newValuesMap, oldValuesMap) => {
			const missingValuesMap = {};

			for (const columnId in newValuesMap) {
				const newValues = newValuesMap[columnId];
				const oldValues = oldValuesMap[columnId];

				if (!oldValues) {
					missingValuesMap[columnId] = newValues;
				} else {
					const difference = newValues.filter(x => !oldValues.includes(x));
					if (difference.length > 0) {
						missingValuesMap[columnId] = difference;
					}
				}
			}

			return missingValuesMap;
		},
		[]
	)

	const fetchMissingItems = useCallback(
		async () => {
			const newValuesMap = getUniqueColumnValues();
			const oldValuesMap = currentColumnValuesMapRef.current;

			// compare and find what needs to be fetched
			const missingValuesMap = getMissingColumnValues(newValuesMap, oldValuesMap);

			const promises: Promise<IdNameResponseItemsResponseModel>[] = [];
			const columnIds: string[] = [];
			for (let columnId in missingValuesMap) {
				const column = visibleColumns.find((item) => item.id === columnId);
				const missingValues = missingValuesMap[columnId];
				if (column instanceof RemoteOptionColumnModel || column instanceof RemoteOptionsColumnModel) {
					const promise = column.fetchInitialItems(missingValues);
					promises.push(promise);
					columnIds.push(columnId);
				}
			}

			setFetchingColumns(columnIds);
			const responses = await Promise.all(promises);
			setFetchingColumns([]);
			const missingColumnItemsMap = {};

			for (let i = 0; i < responses.length; i++) {
				const response = responses[i];
				const columnId = columnIds[i];
				if (response.success) {
					missingColumnItemsMap[columnId] = response.items;
				}
			}

			setColumnValuesMap((state) => {
				const valuesMap = { ...state };
				for (const columnId in missingValuesMap) {
					valuesMap[columnId] = [
						...state[columnId] || [],
						...missingValuesMap[columnId] || []
					]
				}

				return valuesMap;
			});
			setColumnItemsMap((state) => {
				const itemsMap = { ...state };
				for (const columnId in missingColumnItemsMap) {
					itemsMap[columnId] = [
						...state[columnId] || [],
						...missingColumnItemsMap[columnId] || []
					]
				}

				return itemsMap;
			});
		},
		[getMissingColumnValues, getUniqueColumnValues, visibleColumns]
	)

	useEffect(
		() => {
			fetchMissingItems();
		},
		[fetchMissingItems]
	)

	return {
		fetchingColumns,
		columnItemsMap
	}
}
